/**
 * Unit Conversion Utilities
 * Pure functions for converting between different measurement units
 */

// Project-specific conversion factor based on design requirements
// This ensures proper scaling between mm-based designs and pixel-based rendering
const PX_PER_MM = 3.7795275591 // Standard 96 DPI conversion

/**
 * Convert pixels to millimeters
 */
export const pxToMm = (px: number): number => {
  return px / PX_PER_MM
}

/**
 * Convert millimeters to pixels
 */
export const mmToPx = (mm: number): number => {
  return mm * PX_PER_MM
}

/**
 * Convert centimeters to millimeters
 */
export const cmToMm = (cm: number): number => {
  return cm * 10
}

/**
 * Round to specified decimal places
 */
export const roundTo = (value: number, decimals: number = 2): number => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)
}

/**
 * Measurement type with unit
 */
export type Measurement = {
  value: number
  unit: 'px' | 'mm' | 'cm'
}

/**
 * Convert measurement to pixels
 */
export const measurementToPx = (measurement: Measurement): number => {
  switch (measurement.unit) {
    case 'px':
      return measurement.value
    case 'mm':
      return mmToPx(measurement.value)
    case 'cm':
      return mmToPx(cmToMm(measurement.value))
  }
}

/**
 * Convert measurement to millimeters
 */
export const measurementToMm = (measurement: Measurement): number => {
  switch (measurement.unit) {
    case 'px':
      return pxToMm(measurement.value)
    case 'mm':
      return measurement.value
    case 'cm':
      return cmToMm(measurement.value)
  }
}

/**
 * Calculate DPI from image dimensions
 * @param widthPx - Image width in pixels
 * @param heightPx - Image height in pixels
 * @param widthMm - Physical width in millimeters
 * @param heightMm - Physical height in millimeters
 * @returns Average DPI (dots per inch)
 */
export const calculateDPI = (
  widthPx: number,
  heightPx: number,
  widthMm: number,
  heightMm: number,
): number => {
  const widthInches = widthMm / 25.4
  const heightInches = heightMm / 25.4
  const dpiX = widthPx / widthInches
  const dpiY = heightPx / heightInches
  return Math.round((dpiX + dpiY) / 2)
}

/**
 * Calculate DPI from pixels and actual print dimensions
 * @param imagePxWidth - Image width in pixels
 * @param imagePxHeight - Image height in pixels
 * @param printWidthMm - Actual print width in millimeters
 * @param printHeightMm - Actual print height in millimeters
 * @returns Calculated DPI based on image resolution and print size
 */
export const calculateImageDPI = (
  imagePxWidth: number,
  imagePxHeight: number,
  printWidthMm?: number,
  printHeightMm?: number,
): number => {
  // If no print dimensions provided, use the image's natural dimensions
  // converted to mm (this gives us the effective DPI if printed at natural size)
  const actualPrintWidthMm = printWidthMm ?? pxToMm(imagePxWidth)
  const actualPrintHeightMm = printHeightMm ?? pxToMm(imagePxHeight)

  // Calculate what DPI this image has for the given print dimensions
  return calculateDPI(imagePxWidth, imagePxHeight, actualPrintWidthMm, actualPrintHeightMm)
}

/**
 * Check if image resolution meets minimum quality requirements
 * @param dpi - The DPI to check
 * @param minDpi - Minimum required DPI (default: 100)
 * @returns true if quality is acceptable
 */
export const isAcceptableImageQuality = (dpi: number, minDpi: number = 100): boolean => {
  return dpi >= minDpi
}

/**
 * Convert SVG path coordinates from mm to px
 * Parses SVG path commands and converts coordinate values from mm to px
 * Preserves arc flags (0 and 1) as-is
 * @param pathData - SVG path data string with mm coordinates
 * @returns SVG path data string with px coordinates
 */
export const convertPathMmToPx = (pathData: string): string => {
  // Parse path into segments by command letters
  const segments = pathData.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g) || []

  return segments
    .map((segment) => {
      const command = segment[0]
      const args = segment.slice(1).trim()

      if (!args || command.toLowerCase() === 'z') {
        return segment // No arguments to convert for Z/z command
      }

      // Parse numbers from the segment
      const numbers = args.match(/-?\d+\.?\d*/g) || []

      // For Arc command (A/a), handle special flag positions
      if (command.toLowerCase() === 'a') {
        // Arc: rx ry x-axis-rotation large-arc-flag sweep-flag x y
        // Indices 3 and 4 are flags (0 or 1) and should not be converted
        const converted = numbers.map((num, index) => {
          const value = parseFloat(num)
          // For each set of 7 parameters, indices 3 and 4 are flags
          const paramIndex = index % 7
          if (paramIndex === 3 || paramIndex === 4) {
            return num // Keep flags as-is (0 or 1)
          }
          // Convert mm to px for actual coordinates and radii
          return roundTo(mmToPx(value), 2).toString()
        })
        return `${command} ${converted.join(' ')}`
      }

      // For other commands, convert all numbers
      const converted = numbers.map((num) => {
        const value = parseFloat(num)
        return roundTo(mmToPx(value), 2).toString()
      })

      return `${command} ${converted.join(' ')}`
    })
    .join(' ')
}
